import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { Network } from '@capacitor/network';
import { $log } from '@/common/api/log.service';
import { LOG_ERROR, LOG_WARNING, LOG_INFO } from '@/common/log/log';
import { isResourceGone } from '@/common/api/resourceGone';

// Flips to true when the page is being unloaded or hidden so we can suppress
// logging for requests the browser cancels on our behalf. Reset on visibility
// restore so a backgrounded tab returning to focus resumes normal logging.
let pageUnloading = false;
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
        pageUnloading = true;
    });
    window.addEventListener('beforeunload', () => {
        pageUnloading = true;
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            pageUnloading = false;
        }
    });
}

function redactHeaders(headers) {
    // Handles both plain objects and axios 1.x AxiosHeaders instances.
    // AxiosHeaders doesn't store keys as own enumerable properties, so a
    // naive spread returns {} — we have to normalize via toJSON / entries.
    let normalized = {};
    if (headers && typeof headers.toJSON === 'function') {
        normalized = { ...headers.toJSON() };
    } else if (headers && typeof headers === 'object') {
        normalized = { ...headers };
    }
    for (const key of Object.keys(normalized)) {
        if (key.toLowerCase() === 'authorization') {
            normalized[key] = '[redacted]';
        }
    }
    return normalized;
}

// A 403 on a GET of /User or /User/{id} while the viewer carries the
// restrictToSharedLocations flag (#4582/#4616) is the shared-location access
// policy working as designed — the target simply isn't at one of the viewer's
// locations. Surfacing the generic permission snackbar for it reads as an
// error, so these are suppressed (the request is still logged at warning
// level below). Non-GET methods and other resource types keep the snackbar:
// a restricted user failing to WRITE a User, or being denied something
// unrelated, is a real permission problem worth showing.
export function isLocationRestricted403(err, authStore) {
    if (err?.response?.status !== 403) return false;
    if (authStore.user?.user?.restrictToSharedLocations !== true) return false;
    const method = (err.config?.method || '').toLowerCase();
    if (method && method !== 'get') return false;
    const url = err.config?.url || '';
    // Match "User" as an exact path segment (User?... , /User/abc, full URLs) —
    // not other resources that merely start with the word (e.g. /UserInvite).
    return /(^|\/)User(\/[^/?#]+)?([?#]|$)/.test(url);
}

export { isResourceGone };

// A caller that dereferences a reference that may dangle (see isResourceGone) opts in
// with { notFoundOk: true } and renders the target as missing, so the "Bad request"
// snackbar would only alarm the user about something they cannot fix. Still logged
// below, so dangling references stay visible in production logs.
export function isToleratedNotFound(err) {
    return err?.config?.notFoundOk === true && isResourceGone(err);
}

// Aidbox rejects a FHIR transaction bundle for authorization by failing the whole
// transaction: the browser sees HTTP 400 whose OperationOutcome text embeds the real
// inner status — "Transaction failed at entry[0]. Response status is 403" (#5637).
// Without unwrapping it, the outer 400 falls through to the generic bad-request
// notification, which tells the user to try again — the one thing that cannot work,
// and exactly the wall users were observed retrying into.
//
// Returns the failing entry index, or null when this is not that shape.
export function forbiddenTransactionEntry(err) {
    if (err?.response?.status !== 400) return null;
    const outcome = err.response.data;
    if (outcome?.resourceType !== 'OperationOutcome') return null;
    const match = /Transaction failed at entry\[(\d+)\]\. Response status is 403/.exec(outcome?.text?.div ?? '');
    return match ? Number(match[1]) : null;
}

// A timeout is a client-side abort, not a server failure. It must not be detected
// by rebuilding axios' message string ("timeout of 30000ms exceeded"): that string
// embeds the effective timeout, so the match broke the moment file uploads were
// given a per-request budget of their own (#5665), and every timed-out upload fell
// through to the generic "problem with the server" message instead. axios reports
// ECONNABORTED for a timed-out request, or ETIMEDOUT when the transitional
// clarifyTimeoutError option is enabled.
export function isTimeoutError(err) {
    return err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
}

// A denied bundle is terminal for the user — the message must not invite a retry.
const FORBIDDEN_TRANSACTION_MESSAGE = 'You do not have permission to complete this action. Retrying will not help. Please contact your support.';

// A 401 for a rejected token carries one of three messages. 'JWT is expired' is Aidbox's, on
// FHIR requests; 'Expired JWT Token' and 'Invalid JWT Token' are this API's (Lexik).
const EXPIRED_TOKEN_MESSAGES = ['JWT is expired', 'Expired JWT Token'];
const INVALID_TOKEN_MESSAGE = 'Invalid JWT Token';

// JWTDecodedListener adds this reason to 'Invalid JWT Token' only when it rejected the token
// because a newer one was issued to the user (the single-session rule). That is the one case
// the "another device" message is true for (#6257).
const SESSION_SUPERSEDED_REASON = 'session_superseded';

export const SINGLE_SESSION_LOCKOUT_DESCRIPTION = 'Your screen has been locked since logging in on another device.  Only 1 browser can be logged in at a time.';
export const SESSION_EXPIRED_LOCKOUT_DESCRIPTION = 'BestNotes has been locked because your session expired';

function isRejectedToken(err) {
    if (err?.response?.status !== 401) return false;
    const message = err.response.data?.message;
    return message === INVALID_TOKEN_MESSAGE || EXPIRED_TOKEN_MESSAGES.includes(message);
}

function bearerToken(headers) {
    const value = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization;
    return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice('Bearer '.length) : null;
}

// Whether a request rejected for its token can go again with a current one (#6257). Its token
// was attached when it was sent, so after a laptop wakes, requests fired in the same moment as
// the refresh carry the expired token while the refresh succeeds.
async function canRetryWithCurrentToken(err, authStore) {
    if (err.config?.retriedAfterTokenRejection) return false;

    const rejectedToken = bearerToken(err.config?.headers);
    if (!rejectedToken || !authStore.token) return false;

    // A newer token arrived while the request was on the wire: a refresh landed, a sign-in
    // finished, or another tab shared one.
    if (authStore.token !== rejectedToken) return true;

    // The request carried the newest token there is. Refreshing cures expiry only; a token
    // rejected as invalid or superseded stays rejected.
    if (!EXPIRED_TOKEN_MESSAGES.includes(err.response.data?.message)) return false;
    try {
        await authStore.refreshAuthToken();
    } catch {
        // The refresh itself was rejected: the session genuinely cannot be renewed.
        return false;
    }
    return !!authStore.token && authStore.token !== rejectedToken;
}

function classifyLogSeverity(err, isOffline = false) {
    // Returns a LOG_* level, or null to skip logging entirely.
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.__CANCEL__) {
        return null;
    }
    // Skip noise from requests cancelled by page navigation/unload, and
    // from requests that fail because the user is fully offline (the user
    // already gets a notification — logging is redundant).
    if (pageUnloading) return null;
    if (isOffline && !err?.response) return null;

    const status = err?.response?.status;
    if (!status) {
        // No response: network error, timeout, DNS failure, CORS, etc.
        // The browser deliberately hides the cause, so we can't tell a
        // transient blip from a server outage. Log at warning level so
        // individual entries aren't treated as actionable errors.
        return LOG_WARNING;
    }
    if (status >= 500) return LOG_ERROR;
    if (status === 401 || status === 403 || status === 404) return LOG_WARNING;
    if (status >= 400) return LOG_ERROR;
    return LOG_INFO;
}

/**
 * @param {Error} err - The axios error.
 * @param {import('axios').AxiosInstance|null} client - The instance that sent the request, so a
 *   request rejected for a stale token can be sent again through it.
 */
export async function bnHttpErrorHandler(err, client = null) {
    // Add guard against recursive error handling
    if (err?.config?.url?.includes('/log')) {
        // Skip error handling for logging endpoints to prevent infinite loops
        return Promise.reject(err);
    }

    const authStore = useAuthStore();
    let notifications = [];
    console.error(err);

    let status;
    try {
        status = await Network?.getStatus();
    } catch {
        /* empty */
    }
    const isOffline = !!(status && !status.connected && status.connectionType === 'none');
    if (isOffline) {
        notifications.push({
            message: 'Please check your internet connection',
            color: 'error',
        });
    } else if (err?.message?.msg === 'Network Error' || err?.message === 'Network Error') {
        notifications.push({
            message: 'Cannot reach the server, please check your internet connection. Please try again later or contact support if problem persists.',
            color: 'error',
        });
    } else if (isTimeoutError(err)) {
        notifications.push({
            // A file transfer that ran out of budget is not a slow server, and telling
            // the user to reload the page is useless advice for one — give the transfer
            // its own wording so a timeout is distinguishable from a server error.
            message: err?.config?.isFileUpload
                ? 'The upload took too long and was stopped, so nothing was attached. Try again, or try a smaller file if you are on a slow connection. If this problem persists please contact BestNotes for support.'
                : 'The server took too long to respond. You can wait a bit and try again or reload this page.  If this problem persists please contact BestNotes for support.',
            color: 'error',
        });
    } else if (isRejectedToken(err)) {
        // Retry before touching the redirect latch: a retry that succeeds navigates nowhere, and
        // only arriving at the lockout or login page releases the latch. The request interceptor
        // attaches the current token, and the caller gets the retried request's outcome.
        if (client && (await canRetryWithCurrentToken(err, authStore))) {
            return client.request({ ...err.config, retriedAfterTokenRejection: true });
        }

        // Already there: pushing again is a duplicate navigation, which never releases the latch.
        const onAuthPage = ['lockout', 'login'].includes(router.currentRoute?.value?.name);
        if (!onAuthPage && authStore.acquireAuthRedirect()) {
            await router.push({
                name: 'lockout',
                // State, not params: Vue Router discards params the lockout path doesn't declare.
                state: {
                    lockoutDescription: err.response.data?.reason === SESSION_SUPERSEDED_REASON ? SINGLE_SESSION_LOCKOUT_DESCRIPTION : SESSION_EXPIRED_LOCKOUT_DESCRIPTION,
                },
            });
        }
    } else if (err?.config && Object.prototype.hasOwnProperty.call(err.config, 'errorHandle') && err.config.errorHandle === false) {
        try {
            const severity = classifyLogSeverity(err, isOffline);
            if (!err?.config?.skipLogging && err.response && severity) {
                const errorTrace = `Status: ${err.response.status}\nURL: ${err.response.config?.url}\nMethod: ${err.response.config?.method}\nRequest Headers: ${JSON.stringify(redactHeaders(err.config?.headers), null, 2)}\nResponse Data: ${JSON.stringify(err.response.data, null, 2)}`;
                $log.log(severity, 'HTTP Request Failed', err.message, errorTrace);
            }
        } catch {
            /* prevent logging failures from blocking error propagation */
        }
        return Promise.reject(err);
    } else if (!err.response) {
        // network error (wasn't able to connect to server)
        notifications.push({
            message: 'There was a problem with the server.  Please try again.',
            color: 'error',
        });
    } else if (isLocationRestricted403(err, authStore)) {
        // Expected denial for a shared-location-restricted viewer — no snackbar.
        // Falls through to the logging block below.
    } else if (isToleratedNotFound(err)) {
        // Caller renders the dangling reference as missing (#5934) — no snackbar.
        // Falls through to the logging block below.
    } else if ((err.response.status === 401 || err.response.status === 403) && authStore.isAuthenticated) {
        notifications.push({
            message: 'You do not have permission to view this content.',
            color: 'error',
        });
    } else if (forbiddenTransactionEntry(err) !== null) {
        // Authorization failure wearing a 400 (#5637) — surface it as the permission
        // problem it is, and do not suggest retrying.
        notifications.push({
            message: FORBIDDEN_TRANSACTION_MESSAGE,
            color: 'error',
        });
    } else if (err.response.data.errors && err.response.data.errors.length) {
        err.response.data.errors.forEach((err) => {
            notifications.push({
                // Entries can be objects ({ userMessage, message }) or plain strings.
                message: err.userMessage || err.message || err,
                color: 'error',
            });
        });
    } else if (err.response.status === 413) {
        // The API caps a POST body at 70 MB (api/frankenphp/conf.d/app.prod.ini).
        // The upload dialog validates sizes before sending, so this is the backstop
        // for the paths that don't — and "bad request, try again" is the wrong advice
        // for a file that will be exactly as large next time (#5487).
        notifications.push({
            message: 'That file is too large to upload. Please try a smaller file, or upload fewer files at once.',
            color: 'error',
        });
    } else if (err.response.status >= 500) {
        notifications.push({
            message: 'There was a problem with the server.  Please try again.',
            color: 'error',
        });
    } else if (err.response.status >= 400) {
        notifications.push({
            message: 'Bad request. Please try again.  If problem persists log off then try again.',
            color: 'error',
        });
    }

    const notificationStore = useNotificationStore();

    notificationStore.add(notifications);

    // Modify the logging section to include additional safety checks
    try {
        const severity = classifyLogSeverity(err, isOffline);
        if (!err?.config?.skipLogging && severity) {
            let errorSummary = 'HTTP Request Failed';
            let errorDescription = err.message;
            let errorTrace = '';

            const route = router.currentRoute?.value?.fullPath;
            if (route) errorTrace += `Route: ${route}\n`;
            if (err?.code) errorTrace += `Code: ${err.code}\n`;

            // Build detailed error trace
            if (err.response) {
                // Server responded with error
                errorTrace += `Status: ${err.response.status}\n`;
                errorTrace += `Status Text: ${err.response.statusText}\n`;
                errorTrace += `URL: ${err.response.config?.url}\n`;
                errorTrace += `Method: ${err.response.config?.method}\n`;
                errorTrace += `Request Headers: ${JSON.stringify(redactHeaders(err.config?.headers), null, 2)}\n`;
                errorTrace += `Response Headers: ${JSON.stringify(err.response.headers, null, 2)}\n`;
                errorTrace += `Response Data: ${JSON.stringify(err.response.data, null, 2)}`;
            } else if (err.request) {
                // Request made but no response
                const url = err.config?.url ?? err.request?.responseURL ?? err.request?._url ?? 'unknown';
                const method = err.config?.method ?? 'unknown';
                errorTrace += `Request made but no response received\n`;
                errorTrace += `URL: ${url}\n`;
                errorTrace += `Method: ${method}`;
                if (err.stack) errorTrace += `\nStack: ${err.stack}`;
            } else {
                // Error in request setup
                errorTrace += err.stack || err.toString();
            }

            $log.log(severity, errorSummary, errorDescription, errorTrace);
        }
    } catch (loggingError) {
        // If logging fails, just console.error and continue
        console.error('Failed to log error:', loggingError);
    }

    return Promise.reject(err);
}
